'use client';

import type { ReactNode } from 'react';
import type { Advisor } from 'src/components/admin/advisor-select';
import type { BulkManageTagsConfirmPayload } from 'src/components/advisor-dashboard/tags/bulk-manage-tags-dialog';
import type { BulkTagInfo } from 'src/components/advisor-dashboard/tags/bulk-tag-types';
import type { FragmentType } from 'src/gql';
import type { TagData } from 'src/types/tag-data';
import { useCallback, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Button } from '@savvy-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@savvy-kit/components/dialog';
import SearchableCombobox from '@savvy-kit/patterns/searchable-combobox';
import { isNil } from 'es-toolkit';
import { Archive, Tags, UserRoundCog, Users } from 'lucide-react';
import { toast } from 'sonner';
import { AdvisorSelect } from 'src/components/admin/advisor-select';
import { BulkManageTagsDialog } from 'src/components/advisor-dashboard/tags/bulk-manage-tags-dialog';
import { graphql, unmaskFragment } from 'src/gql';

import type { BulkAction } from '@savvy/data-table';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import { ClientsTagsFragment, HouseholdClientFragment } from '../fragments';

type AdvisorGroup = {
  readonly id: string;
  readonly name: string;
  readonly representationType: string;
  readonly members: readonly {
    readonly id: string;
    readonly user: {
      readonly id: string;
      readonly firstName: string;
      readonly lastName: string;
      readonly role: string;
    };
  }[];
};

type HouseholdRow = FragmentType<typeof HouseholdClientFragment>;

const UpdateHouseholdTagsMutation = graphql(/* GraphQL */ `
  mutation BulkUpdateHouseholdTags($input: UpdateHouseholdTagsInput!) {
    updateHouseholdTags(input: $input) {
      household {
        id
        householdTags {
          id
          organizationHouseholdTag {
            id
            name
            organizationHouseholdCategory {
              hexColorCode
              emoji
            }
          }
        }
      }
    }
  }
`);

const ShareHouseholdWithAdvisorGroupMutation = graphql(/* GraphQL */ `
  mutation BulkShareHouseholdWithAdvisorGroup($input: ShareHouseholdWithAdvisorGroupInput!) {
    shareHouseholdWithAdvisorGroup(input: $input) {
      success
      errors
    }
  }
`);

const MoveClientToAdvisorMutation = graphql(/* GraphQL */ `
  mutation BulkMoveClientToAdvisor($input: MoveClientToAdvisorInput!) {
    moveClientToAdvisor(input: $input) {
      success
    }
  }
`);

const ArchiveHouseholdMutation = graphql(/* GraphQL */ `
  mutation BulkArchiveHousehold($input: ArchiveHouseholdInput!) {
    archiveHousehold(input: $input) {
      success
    }
  }
`);

function getExistingOrgTagIds(row: HouseholdRow): string[] {
  const household = unmaskFragment(HouseholdClientFragment, row);
  const tagsData = unmaskFragment(ClientsTagsFragment, household);
  const householdTags = tagsData?.householdTags;
  if (!householdTags) return [];
  return householdTags.map((ht) => ht?.organizationHouseholdTag?.id).filter((id): id is string => !isNil(id));
}

export function mergeTagIds(existingIds: readonly string[], tagIdsToAdd: readonly string[]): string[] {
  return Array.from(new Set([...existingIds, ...tagIdsToAdd]));
}

export function removeTagIds(existingIds: readonly string[], tagIdsToRemove: ReadonlySet<string>): string[] {
  return existingIds.filter((id) => !tagIdsToRemove.has(id));
}

export function applyTagChanges(
  existingIds: readonly string[],
  tagIdsToAdd: readonly string[],
  tagIdsToRemove: ReadonlySet<string>,
): string[] {
  return mergeTagIds(removeTagIds(existingIds, tagIdsToRemove), tagIdsToAdd);
}

function getHouseholdName(row: HouseholdRow): string {
  return unmaskFragment(HouseholdClientFragment, row).name;
}

function getPrimaryClientUserId(row: HouseholdRow): string | null {
  const household = unmaskFragment(HouseholdClientFragment, row);
  return household.primaryClient?.user.id ?? null;
}

async function runBulkOperation<T>({
  ids,
  operation,
  label,
}: {
  ids: string[];
  operation: (id: string) => Promise<T>;
  label: string;
}): Promise<{ succeeded: number; failed: number }> {
  const loadingToast = toast.loading(`${label} (0/${ids.length})...`);
  let succeeded = 0;
  let failed = 0;

  // Sequential, not Promise.all: SyncHouseholdTags takes a row lock on the
  // organization_household_tags row (household_count counter), so adding the
  // same tag to N households concurrently deadlocks. Serialize to avoid it.
  for (const id of ids) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await operation(id);
      succeeded += 1;
    } catch {
      failed += 1;
    }
    toast.loading(`${label} (${succeeded + failed}/${ids.length})...`, { id: loadingToast });
  }

  toast.dismiss(loadingToast);
  return { succeeded, failed };
}

function showBulkResult(label: string, succeeded: number, failed: number) {
  if (failed === 0) {
    toast.success(`${label}: ${succeeded} household${succeeded === 1 ? '' : 's'} updated`);
  } else if (succeeded === 0) {
    toast.error(`${label}: all ${failed} household${failed === 1 ? '' : 's'} failed`);
  } else {
    toast.error(`${label}: ${succeeded} succeeded, ${failed} failed`);
  }
}

type UseClientsBulkActionsParams = {
  readonly households: readonly HouseholdRow[];
  readonly availableTags: readonly TagData[];
  readonly advisorGroups: readonly AdvisorGroup[] | null;
  readonly onComplete: () => void;
};

type BulkDialog = 'manageTags' | 'advisorGroup' | 'changeAdvisor' | 'archive' | null;

export function useClientsBulkActions({
  households,
  availableTags,
  advisorGroups,
  onComplete,
}: UseClientsBulkActionsParams) {
  const client = useApolloClient();
  const [updateHouseholdTags] = useMutationWithErrorLogging(UpdateHouseholdTagsMutation);
  const [shareWithAdvisorGroup] = useMutationWithErrorLogging(ShareHouseholdWithAdvisorGroupMutation);
  const [moveClientToAdvisor] = useMutationWithErrorLogging(MoveClientToAdvisorMutation);
  const [archiveHousehold] = useMutationWithErrorLogging(ArchiveHouseholdMutation);

  const [activeDialog, setActiveDialog] = useState<BulkDialog>(null);
  const [pendingIds, setPendingIds] = useState<string[]>([]);

  const [selectedAdvisorGroupId, setSelectedAdvisorGroupId] = useState<string | null>(null);
  const [selectedAdvisor, setSelectedAdvisor] = useState<Advisor | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const householdsById = useMemo(() => {
    const map = new Map<string, HouseholdRow>();
    for (const h of households) {
      const unmasked = unmaskFragment(HouseholdClientFragment, h);
      map.set(unmasked.id, h);
    }
    return map;
  }, [households]);

  const resetDialogState = useCallback(() => {
    setSelectedAdvisorGroupId(null);
    setSelectedAdvisor(null);
    setIsSaving(false);
  }, []);

  const openDialog = useCallback((dialog: BulkDialog, ids: string[]) => {
    setPendingIds(ids);
    setActiveDialog(dialog);
  }, []);

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    resetDialogState();
  }, [resetDialogState]);

  const evictAndComplete = useCallback(() => {
    // Refetch the list explicitly. Evicting the field alone didn't reliably
    // repull every affected row, so a multi-household update could leave all
    // but the last-written row stale in the table.
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'advisorDashboardClients' });
    client.cache.gc();
    void client.refetchQueries({ include: ['ClientsPage'] });
    closeDialog();
    onComplete();
  }, [client, closeDialog, onComplete]);

  const handleManageTags = useCallback(
    async ({ tagsToAdd, tagsToRemove }: BulkManageTagsConfirmPayload) => {
      const removeSet = new Set(tagsToRemove);
      const { succeeded, failed } = await runBulkOperation({
        ids: pendingIds,
        label: 'Updating tags',
        operation: async (householdId) => {
          const row = householdsById.get(householdId);
          const existingIds = row ? getExistingOrgTagIds(row) : [];
          const nextIds = applyTagChanges(existingIds, tagsToAdd, removeSet);
          await updateHouseholdTags({
            variables: { input: { householdId, organizationHouseholdTagIds: nextIds } },
          });
        },
      });

      showBulkResult('Tags', succeeded, failed);
      evictAndComplete();
    },
    [pendingIds, householdsById, updateHouseholdTags, evictAndComplete],
  );

  const handleBulkAssignAdvisorGroup = useCallback(async () => {
    if (isNil(selectedAdvisorGroupId)) return;
    setIsSaving(true);

    const { succeeded, failed } = await runBulkOperation({
      ids: pendingIds,
      label: 'Assigning advisor group',
      operation: async (householdId) => {
        await shareWithAdvisorGroup({
          variables: { input: { householdId, advisorGroupId: selectedAdvisorGroupId } },
        });
      },
    });

    showBulkResult('Assign advisor group', succeeded, failed);
    evictAndComplete();
  }, [selectedAdvisorGroupId, pendingIds, shareWithAdvisorGroup, evictAndComplete]);

  const handleBulkChangeAdvisor = useCallback(async () => {
    if (isNil(selectedAdvisor)) return;
    setIsSaving(true);

    const { succeeded, failed } = await runBulkOperation({
      ids: pendingIds,
      label: 'Changing primary advisor',
      operation: async (householdId) => {
        const row = householdsById.get(householdId);
        const clientUserId = row ? getPrimaryClientUserId(row) : null;
        if (isNil(clientUserId)) throw new Error('No primary client');
        await moveClientToAdvisor({
          variables: {
            input: {
              clientUserId,
              newAdvisorUserId: selectedAdvisor.user.id,
            },
          },
        });
      },
    });

    showBulkResult('Change primary advisor', succeeded, failed);
    evictAndComplete();
  }, [selectedAdvisor, pendingIds, householdsById, moveClientToAdvisor, evictAndComplete]);

  const handleBulkArchive = useCallback(async () => {
    setIsSaving(true);

    const { succeeded, failed } = await runBulkOperation({
      ids: pendingIds,
      label: 'Archiving',
      operation: async (householdId) => {
        await archiveHousehold({
          variables: { input: { id: householdId } },
        });
      },
    });

    showBulkResult('Archive', succeeded, failed);
    evictAndComplete();
  }, [pendingIds, archiveHousehold, evictAndComplete]);

  const nonSmartTags = useMemo(() => availableTags.filter((t) => t.isSmart !== true), [availableTags]);

  // Tags actually applied to at least one selected household — the only ones
  // worth offering in Remove Tags (removing an unapplied tag is a no-op).
  const removableTagIds = useMemo(() => {
    const ids = new Set<string>();
    for (const householdId of pendingIds) {
      const row = householdsById.get(householdId);
      if (row) for (const id of getExistingOrgTagIds(row)) ids.add(id);
    }
    return ids;
  }, [pendingIds, householdsById]);

  // Removable list must be sourced from nonSmartTags, never the full catalog:
  // smart (rule-based) tags are auto-assigned and must not be manually removable.
  const presentTags = useMemo<readonly BulkTagInfo[]>(
    () => nonSmartTags.filter((t) => removableTagIds.has(t.id)),
    [nonSmartTags, removableTagIds],
  );

  const groupTypeAdvisorGroups = useMemo(
    () => (advisorGroups ?? []).filter((g) => g.representationType === 'group'),
    [advisorGroups],
  );

  const advisorGroupOptions = useMemo(
    () => groupTypeAdvisorGroups.map((g) => ({ value: g.id, label: g.name })),
    [groupTypeAdvisorGroups],
  );

  const actions: readonly BulkAction[] = useMemo(
    () => [
      {
        label: 'Tags',
        icon: <Tags className="size-4" />,
        onClick: (ids: string[]) => openDialog('manageTags', ids),
      },
      {
        label: 'Advisor Group',
        icon: <Users className="size-4" />,
        onClick: (ids: string[]) => openDialog('advisorGroup', ids),
      },
      {
        label: 'Primary Advisor',
        icon: <UserRoundCog className="size-4" />,
        onClick: (ids: string[]) => openDialog('changeAdvisor', ids),
      },
      {
        label: 'Archive',
        icon: <Archive className="size-4" />,
        destructive: true,
        onClick: (ids: string[]) => openDialog('archive', ids),
      },
    ],
    [openDialog],
  );

  const householdNames = useMemo(() => {
    return pendingIds
      .map((id) => {
        const row = householdsById.get(id);
        return row ? getHouseholdName(row) : null;
      })
      .filter((n): n is string => !isNil(n));
  }, [pendingIds, householdsById]);

  const dialogs: ReactNode = (
    <>
      <BulkManageTagsDialog
        open={activeDialog === 'manageTags'}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        selectedCount={pendingIds.length}
        availableTags={availableTags}
        presentTags={presentTags}
        onConfirm={handleManageTags}
        entityNoun="household"
        entityNounPlural="households"
      />

      {/* Assign Advisor Group Dialog */}
      <Dialog
        open={activeDialog === 'advisorGroup'}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="bg-popover" initialFocus={false}>
          <DialogTitle className="text-foreground">
            Assign advisor group to {pendingIds.length} household{pendingIds.length === 1 ? '' : 's'}
          </DialogTitle>
          <div className="mt-3">
            <DialogDescription className="text-secondary mb-2">
              All members of the selected team will have access to the selected households.
            </DialogDescription>
            <SearchableCombobox
              id="bulk-advisor-group-select"
              options={advisorGroupOptions}
              value={selectedAdvisorGroupId ?? ''}
              onValueChange={(id) => setSelectedAdvisorGroupId(id || null)}
              placeholder="Select an advisor group"
              emptyMessage="No advisor groups found"
            />
          </div>
          <DialogFooter>
            <DialogClose
              render={
                <Button variant="secondary" disabled={isSaving}>
                  Cancel
                </Button>
              }
            />
            <Button onClick={handleBulkAssignAdvisorGroup} disabled={isSaving || isNil(selectedAdvisorGroupId)}>
              {isSaving ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Primary Advisor Dialog */}
      <Dialog
        open={activeDialog === 'changeAdvisor'}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="bg-popover sm:max-w-lg" initialFocus={false}>
          <DialogTitle className="text-foreground">
            Change primary advisor for {pendingIds.length} household{pendingIds.length === 1 ? '' : 's'}
          </DialogTitle>
          <div className="mt-3">
            <DialogDescription className="text-secondary mb-2">
              The selected advisor will become the primary advisor for all selected households.
            </DialogDescription>
            <AdvisorSelect
              onAdvisorSelected={setSelectedAdvisor}
              selectedAdvisor={selectedAdvisor}
              roleFilter="ADVISOR"
            />
          </div>
          <DialogFooter>
            <DialogClose
              render={
                <Button variant="secondary" disabled={isSaving}>
                  Cancel
                </Button>
              }
            />
            <Button onClick={handleBulkChangeAdvisor} disabled={isSaving || isNil(selectedAdvisor)}>
              {isSaving ? 'Updating...' : 'Change Advisor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={activeDialog === 'archive'}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="bg-popover" initialFocus={false}>
          <DialogTitle className="text-foreground">
            Archive {pendingIds.length} household{pendingIds.length === 1 ? '' : 's'}
          </DialogTitle>
          <div className="mt-3">
            <DialogDescription className="text-secondary">
              {pendingIds.length === 1
                ? `Are you sure you want to archive ${householdNames[0] ?? 'this household'}?`
                : `Are you sure you want to archive these ${pendingIds.length} households?`}{' '}
              Archived households can be restored from the archived list.
            </DialogDescription>
          </div>
          <DialogFooter>
            <DialogClose
              render={
                <Button variant="secondary" disabled={isSaving}>
                  Cancel
                </Button>
              }
            />
            <Button variant="destructive" onClick={handleBulkArchive} disabled={isSaving}>
              {isSaving ? 'Archiving...' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return { actions, dialogs };
}
